import type { LucideIcon } from 'lucide-react';
import { PenLine, CheckCircle2, Trash2 } from 'lucide-react';

export const STATUS_CONFIG: Record<string, { style: string; icon: LucideIcon }> = {
  draft: { style: 'bg-blue-100 text-blue-800 border-blue-300', icon: PenLine },
  pending_finalisation: { style: 'bg-amber-100 text-amber-800 border-amber-300', icon: PenLine },
  final: { style: 'bg-emerald-100 text-emerald-800 border-emerald-300', icon: CheckCircle2 },
  pending_disposal: { style: 'bg-amber-100 text-amber-800 border-amber-300', icon: CheckCircle2 },
  archived: { style: 'bg-purple-100 text-purple-800 border-purple-300', icon: CheckCircle2 },
  destroyed: { style: 'bg-red-100 text-red-800 border-red-300', icon: Trash2 },
  deleted: { style: 'bg-red-100 text-red-800 border-red-300', icon: Trash2 },
};

export const DEFAULT_STATUS_CONFIG = { style: 'bg-gray-100 text-gray-700 border-gray-300', icon: CheckCircle2 };

export const ISC_STYLES: Record<string, string> = {
  unclassified: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  'in-confidence': 'bg-amber-100 text-amber-700 border-amber-300',
  sensitive: 'bg-red-100 text-red-700 border-red-300',
  restricted: 'bg-red-300 text-black border-red-500',
};

export const ISC_LEVELS = Object.keys(ISC_STYLES);

export const formatIscLevelName = (level?: string): string => {
  const value = (level || 'unclassified').toLowerCase();
  return value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join('-');
};

const ISC_ROW_BACKGROUNDS: Record<string, string> = {
  unclassified: 'bg-white',
  'in-confidence': 'bg-amber-50/50',
  sensitive: 'bg-red-50/50',
  restricted: 'bg-red-100/80',
};

export const getIscRowBackground = (level?: string): string =>
  ISC_ROW_BACKGROUNDS[level?.toLowerCase() || ''] ?? ISC_ROW_BACKGROUNDS.unclassified;

export const ISC_DESCRIPTIONS: Record<string, string> = {
  unclassified:
    'Accessible to anyone without authentication.\n\nUse for information with no access restrictions that is appropriate for public release.',
  'in-confidence':
    'Intended for PNCC staff only unless released under a LGOIMA request.\n\nUse for all personal information provided by users.',
  sensitive:
    'Access restricted to authorised staff.\n\nUse for information whose compromise could damage national interests or endanger citizens.',
  restricted:
    'Access restricted to specifically authorised staff.\n\nUse for national security information whose compromise could harm New Zealand.',
};

export const PSR_CLASSIFICATION_URL =
  'https://www.digital.govt.nz/standards-and-guidance/governance/managing-online-channels/security-and-privacy-for-websites/foundations/classify-information';

const CONTENT_REMOVED_STATUSES = ['destroyed', 'deleted'] as const;

/** snake_case → Title Case (e.g. "pending_finalisation" → "Pending Finalisation") */
export const formatStatusLabel = (status?: string): string => {
  if (!status) return 'Unknown';
  return status
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

/** snake_case external-reference key → Title Case, with "id" as "ID". */
export const formatExternalReferenceLabel = (key: string): string =>
  key
    .split('_')
    .filter(Boolean)
    .map((word) =>
      word.toLowerCase() === 'id'
        ? 'ID'
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');

export const getIscStyle = (level?: string): string =>
  ISC_STYLES[level?.toLowerCase() || ''] ?? ISC_STYLES.unclassified;

export const getIscDescription = (level?: string): string =>
  ISC_DESCRIPTIONS[level?.toLowerCase() || ''] ?? ISC_DESCRIPTIONS.unclassified;

export const PII_SENSITIVITY_OPTIONS = [
  {
    value: 'standard',
    label: 'Standard',
    description:
      'Names/contact details in professional contexts (e.g., meeting minutes, business reports). Low risk if exposed.',
  },
  {
    value: 'sensitive',
    label: 'Sensitive',
    description:
      'Information where disclosure could cause harm, distress, or embarrassment (e.g., financial hardship, health, dog control disputes, noise complaints, vulnerable children). High risk if exposed.',
  },
] as const;

export const getPiiSensitivityDescription = (level?: string): string => {
  const match = PII_SENSITIVITY_OPTIONS.find(
    (opt) => opt.value === level?.toLowerCase(),
  );
  return match?.description ?? (level ? `Sensitivity level: ${level}` : '');
};

/**
 * Maps frontend edit-state field keys to backend PATCH API field names.
 * Keep in sync with backend EDITABLE_FIELDS in src/ops/record_editing.py.
 */
export const EDITABLE_FIELD_MAP: Record<string, string> = {
  title: 'title',
  content_summary: 'content_summary',
  record_classification_id: 'record_classification_id',
  function_classification_id: 'function_classification_id',
  'information_security_classification.level': 'information_security_classification',
  personal_information: 'personal_information',
  'maori_data_sovereignty.detected': 'maori_data_sovereignty_detected',
  geographical_context: 'geographical_context',
};

/** A record is editable when it is not marked as final (immutability). */
export const isEditable = (immutabilityFinal?: boolean): boolean =>
  immutabilityFinal !== true;

export const isContentRemoved = (status?: string): boolean =>
  CONTENT_REMOVED_STATUSES.includes(
    (status?.toLowerCase() || '') as (typeof CONTENT_REMOVED_STATUSES)[number],
  );
